import Backbone from 'backbone';
import $ from 'jquery';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import Tag from 'components/Tag';
import TagSequence from 'import-sequence/backbone/TagSequence';
import {UploadFile} from 'import-sequence/ImportFromFileView';
import notifications from 'notifications';
import * as keyboard from 'util/Keyboard';
import * as net from 'util/Net';

interface TagSelectModelAttributes {
  idx: number;
}

class TagSelectModel extends Backbone.Model {
  // Used to track the selected index.
  idx!: number;

  override defaults(): TagSelectModelAttributes {
    return {
      idx: -1,
    };
  }
}

// @ts-expect-error: we often use Collections instead of Models to power Views
class TagDropdown extends Backbone.View<Backbone.Collection<UploadFile>> {
  CHECKED!: 1;
  UNCHECKED!: 0;
  INDETERMINATE!: -1;

  selectModel!: TagSelectModel;

  static initClass(): void {
    this.prototype.CHECKED = 1;
    this.prototype.UNCHECKED = 0;
    this.prototype.INDETERMINATE = -1;
  }

  override events(): Backbone.EventsHash {
    return {
      'click .tag-search': function (e) {
        e.stopPropagation();
      },
      'click .tag-options input': 'selectTag',
      'input .tag-search': 'filterTagOptions',
      'keydown .tag-search': 'handleKeydown',
      'change input[type=checkbox]': 'showApply',
      'click .apply-tags': 'applyTags',
      'click .create-tag': 'createTag',
      'click li a': 'applySingleTag',
      'mouseenter li a': 'setSelectedByHover',
    };
  }

  // @ts-expect-error: we often use Collections instead of Models to power Views
  override initialize(options: Backbone.ViewOptions<Backbone.Collection<UploadFile>>): void {
    this.selectModel = new TagSelectModel();
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.selectModel, 'change:idx', this.changeSelected);
    // eslint-disable-next-line no-restricted-syntax, @typescript-eslint/unbound-method
    const showApply = _.bind(this.showApply, this);
    // @ts-expect-error We do have propHooks in jQuery, but we don't have it in our jQuery types
    $.propHooks.checked = {
      set(input: HTMLInputElement, checked: boolean) {
        input.checked = checked;
        if ($(input).parents(this.$el).length > 0) {
          return showApply();
        }
      },
    };
    // @ts-expect-error We do have propHooks in jQuery, but we don't have it in our jQuery types
    $.propHooks.indeterminate = {
      set(input: HTMLInputElement, indeterminate: boolean) {
        input.indeterminate = indeterminate;
        if ($(input).parents(this.$el).length > 0) {
          return showApply();
        }
      },
    };
  }

  checkboxSame($checkbox: JQuery): boolean {
    return (
      ($checkbox.prop('indeterminate') && $checkbox.data('original') === this.INDETERMINATE) ||
      ($checkbox.prop('checked') && $checkbox.data('original') === this.CHECKED) ||
      (!$checkbox.prop('checked') && $checkbox.data('original') === this.UNCHECKED)
    );
  }

  showApply(): Array<never> {
    this.$el.find('.apply-tags').addClass('hidden');
    const result: Array<never> = [];
    for (const el of Array.from(this.$el.find('input[type=checkbox]'))) {
      const $el = $(el);
      if (this.checkboxSame($el)) {
        continue;
      }
      this.$el.find('.apply-tags').removeClass('hidden');
      break;
    }
    return result;
  }

  selectTag(e: JQueryEventObject): void {
    e.stopPropagation();
  }

  setSelectedByHover(e: JQueryEventObject): void {
    const $el = $(e.currentTarget).parent();
    const idx = this.$el.find('.tag-options li').not('.hidden').index($el);
    this.selectModel.set('idx', idx);
  }

  changeSelected(_model: TagSelectModel, idx: number): void {
    this.$el.find('.tag-options li a.selected').removeClass('selected');
    if (idx >= 0) {
      const $li = this.$el.find('.tag-options li').not('.hidden').eq(idx);
      if ($li.length === 0) {
        return;
      }
      $li.find('a').addClass('selected');
      const $parent = $li.parent();
      if ($li[0].offsetTop + $li.height() > $parent[0].scrollTop + $parent.height()) {
        $parent[0].scrollTop = $li[0].offsetTop + $li.height() - $parent.height();
      }
      if ($li[0].offsetTop < $parent[0].scrollTop) {
        $parent[0].scrollTop = $li[0].offsetTop;
      }
    }
  }

  handleKeydown(e: JQueryEventObject): void {
    if (keyboard.isUpKey(e) || keyboard.isDownKey(e)) {
      let idx = this.selectModel.get('idx');
      const visibleLength = this.$el.find('.tag-options li').not('.hidden').length;
      if (keyboard.isUpKey(e)) {
        idx = (idx - 1 + visibleLength) % visibleLength;
      } else if (keyboard.isDownKey(e)) {
        idx = (idx + 1) % visibleLength;
      }
      this.selectModel.set('idx', idx);
      e.preventDefault();
    }
    if (keyboard.isEnterKey(e)) {
      if (this.$el.find('.tag-options li:not(.hidden)').length > 0) {
        this.applySingleTag(e);
      } else if (this.$el.find('.tag-search').val().length > 0) {
        this.createTag();
        this.close();
      }
    }
    if (keyboard.isEscapeKey(e)) {
      this.close();
    }
  }

  close(): void {
    this.$el.dropdown('toggle');
  }

  filterTagOptions(e: JQueryEventObject): void {
    const query = $(e.currentTarget).val().toLowerCase();
    if (query.length > 0) {
      this.$el.find('.tag-options li').each(function () {
        // @ts-expect-error shadows outer this
        const tag = $(this).text().toLowerCase();
        if (tag.indexOf(query.toLowerCase()) === -1) {
          // @ts-expect-error shadows outer this
          $(this).addClass('hidden');
        } else {
          // @ts-expect-error shadows outer this
          $(this).removeClass('hidden');
        }
      });
      this.selectModel.set('idx', 0, {silent: true});
      this.selectModel.trigger('change:idx', this.selectModel, 0);
      const firstTag = this.$el.find('.tag-options li').not('.hidden').eq(0).text();
      if (query !== firstTag.toLowerCase()) {
        this.$el.find('.create-tag').removeClass('disabled');
      } else {
        this.$el.find('.create-tag').addClass('disabled');
      }
    } else {
      this.$el.find('.tag-options li.hidden').removeClass('hidden');
      this.$el.find('.create-tag').addClass('disabled');
    }
  }

  applySingleTag(e: JQueryEventObject): void {
    e.preventDefault();
    e.stopPropagation();
    const $selected = this.$el.find('.tag-options li a.selected');
    const $checkbox = $selected.find('input');
    if ($checkbox.data('original') === this.INDETERMINATE) {
      // If originally indeterminate, toggle through three states.
      if ($checkbox.prop('indeterminate')) {
        $checkbox.prop({
          indeterminate: false,
          checked: false,
        });
      } else if ($checkbox.prop('checked')) {
        $checkbox.prop({
          checked: false,
          indeterminate: true,
        });
      } else {
        $checkbox.prop({
          checked: true,
          indeterminate: false,
        });
      }
    } else {
      // Otherwise, just apply the opposite of the current state and close.
      $checkbox.prop('checked', !$checkbox.prop('checked'));
    }
  }

  getAffected(): Array<TagSequence> {
    throw new Error('getAffected not implemented');
  }

  createTag(): void {
    const newTag = this.$el.find('.tag-search').val();
    const affected = this.getAffected();
    // eslint-disable-next-line no-restricted-syntax
    const sequenceIds = _.pluck(this.getAffected(), 'id');

    const {name, value} = this.getTagNameValue(newTag);
    const values: Record<string, string> = {};
    const addIfNotExists = [];
    if (value != null) {
      values[name] = value;
    } else {
      addIfNotExists.push(name);
    }
    // FIXME: Use `FileBrowserDAO.updateTags` instead
    net
      .postJSON({
        url: '/1/api/files/actions/tag',
        data: {
          ids: sequenceIds,
          toAdd: addIfNotExists,
          toRemove: [],
          toRename: {},
          toSet: values,
        },
      })
      .done(({data}: {data: Array<{id: string; tags: Array<Tag>}>}) => {
        for (const sequence of Array.from(affected)) {
          const datum = _.findWhere(data, {id: sequence.id});
          if (datum == null) {
            continue;
          }
          sequence.get('tags').reset(datum.tags);
        }
        notifications.success('Successfully added tag.');
      })
      .fail(notifications.notifyError);
    analytics.track('data_management', 'batch_modified_sequences', {
      type: 'created_tag',
      sequence_count: affected.length,
    });
  }

  getTagNameValue(string: string): {name: string; value: string | null} {
    const tagRawSplit = string.split(':');
    const tagName = tagRawSplit.shift();
    const tagValue = tagRawSplit.join(':') || null;
    return {name: tagName!, value: tagValue};
  }

  applyTags(): void {
    const addIfNotExists = [];
    const remove = [];
    for (const el of Array.from(this.$el.find('input[type=checkbox]'))) {
      const $el = $(el);
      if (!this.checkboxSame($el)) {
        const tagName = $el.parent().text();
        if ($el.prop('checked')) {
          addIfNotExists.push(tagName);
        } else {
          remove.push(tagName);
        }
      }
    }

    const affected = this.getAffected();
    // eslint-disable-next-line no-restricted-syntax
    const sequenceIds = _.pluck(affected, 'id');
    net
      .postJSON({
        url: '/1/api/files/actions/tag',
        data: {
          ids: sequenceIds,
          toAdd: addIfNotExists,
          toRemove: remove,
          toRename: {},
          toSet: {},
        },
      })
      .done(({data}: {data: Array<{id: string; tags: Array<Tag>}>}) => {
        for (const sequence of Array.from(affected)) {
          const datum = _.findWhere(data, {id: sequence.id});
          if (datum == null) {
            continue;
          }
          sequence.get('tags').reset(datum.tags);
        }
        notifications.success('Applied tags successfully.');
      })
      .fail(notifications.notifyError);
    analytics.track('DATA_MANAGEMENT', 'batch_modified_sequences', {
      type: 'applied_tags',
      sequence_count: affected.length,
      added_count: addIfNotExists.length,
      removed_count: remove.length,
    });
  }

  open(): void {
    this.$el.find('.tag-search').val('').focus();
    this.selectModel.set('idx', -1);
    // Add checked or indeterminate to checkboxes based on tags of selected sequences.
    const tags: Record<string, number> = {};
    const selectedSequences = this.getAffected();
    for (const sequence of selectedSequences) {
      sequence.tags().each((tag: Tag) => {
        const tagName = tag.get('name');
        if (tags[tagName] == null) {
          tags[tagName] = 0;
        }
        return tags[tagName]++;
      });
    }
    const {CHECKED} = this;
    const {UNCHECKED} = this;
    const {INDETERMINATE} = this;
    this.$el.find('.tag-options li').each(function () {
      // @ts-expect-error shadows outer this
      const tag = $(this).text();
      const tagCount = tags[tag] || 0;
      if (tagCount === 0) {
        // @ts-expect-error shadows outer this
        $(this).find('input').prop('checked', false);
        // @ts-expect-error shadows outer this
        $(this).find('input').data('original', UNCHECKED);
      } else if (tagCount === selectedSequences.length) {
        // @ts-expect-error shadows outer this
        $(this).find('input').prop('checked', true);
        // @ts-expect-error shadows outer this
        $(this).find('input').data('original', CHECKED);
      } else {
        // @ts-expect-error shadows outer this
        $(this).find('input').prop('indeterminate', true);
        // @ts-expect-error shadows outer this
        $(this).find('input').data('original', INDETERMINATE);
      }
    });
    this.$el.find('.create-tag').addClass('disabled');
    this.$el.find('.apply-tags').addClass('hidden');
    this.$el.find('.tag-options li.hidden').removeClass('hidden');
  }
}
TagDropdown.initClass();

export default TagDropdown;
